import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Stack, TFCard, TFText } from '@tf/ui';
import type React from 'react';

import { ActionButton, useGetConnectionLabels } from '@/components/shared';
import { ConnectedEntitiesList } from '@/components/structure/ConnectedEntitiesList';
import { hasUniqEntityConnected } from '@/components/structure/hasUniqEntityConnected';
import type { ConnectedEntity, StructureEntity } from '@/components/structure/useStructureEntities';

import { useModalsController } from './ModalsControllerContext';

interface Props {
	connectedEntities: (ConnectedEntity & { connectionKind: string })[];
	mainEntity: StructureEntity;
	entities: StructureEntity[];
	connectionKind: string;
}

export const AddEntityStep: React.FC<Props> = ({ connectedEntities, mainEntity, entities, connectionKind }) => {
	const { _ } = useLingui();
	const { addConnectionModal } = useModalsController() ?? {};

	const getConnectionLabels = useGetConnectionLabels();
	const getLabelsForEntity = getConnectionLabels(mainEntity.entityKind);
	const connectionLabelPlural = getLabelsForEntity(connectionKind).plural;
	const connectionLabelSingular = getLabelsForEntity(connectionKind).singular;

	const hasConnected = hasUniqEntityConnected(entities, connectionKind);

	return (
		<TFCard w="100%" innerPadding="1.5rem">
			<TFText fw="500" fz="14px" lh="18px" mb="12px">
				<Trans id="structure.addEntityStep.label">Please add all {connectionLabelPlural}</Trans>
			</TFText>
			<Stack gap="12px">
				<ConnectedEntitiesList mainEntity={mainEntity} connectedEntities={connectedEntities} entities={entities} />
				{!hasConnected && (
					<ActionButton
						title={_(msg({ id: 'structure.addEntityStep.addButton', message: `Add ${connectionLabelSingular}` }))}
						onClick={() =>
							addConnectionModal?.open({
								connectionKind,
								parentEntityKind: mainEntity.entityKind,
								parentEntityName: mainEntity.entityName,
								parentEntityId: mainEntity.entityId,
								withSuccessMessage: false,
							})
						}
					/>
				)}
			</Stack>
		</TFCard>
	);
};
